import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { Banknote, CreditCard, Divide, CheckCircle2, Ticket, ArrowLeft, Trash2 } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';

const CheckoutPage = () => {
  const { tableNumber } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);

  const [payments, setPayments] = useState([]);
  const [paymentAmountInput, setPaymentAmountInput] = useState('');
  const [posType, setPosType] = useState('harici');
  const [posIp, setPosIp] = useState('');
  const [isWaitingPOS, setIsWaitingPOS] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.user_type !== 'owner') {
          let perms = decoded.permissions || [];
          const expandedPerms = new Set(perms);
          if (perms.includes('orders')) expandedPerms.add('checkout_orders');
          if (!expandedPerms.has('checkout_orders') && !expandedPerms.has('all')) {
            navigate('/admin/dashboard');
            return;
          }
        }
      } catch (e) {
        navigate('/login');
        return;
      }
    } else {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [tableNumber, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get(`/orders/table/${tableNumber}`);
      const activeOrders = response.data.filter(o => ['pending', 'preparing', 'ready'].includes(o.status));
      setOrders(activeOrders);
      
      const total = activeOrders.reduce((sum, order) => sum + order.total_amount, 0);
      setTotalAmount(total);

      try {
        const settingsRes = await apiClient.get('/tenant/settings');
        setPosType(settingsRes.data.pos_type || 'harici');
        setPosIp(settingsRes.data.pos_ip || '');
      } catch (err) {
        console.error('POS ayarları okunamadı:', err);
      }
    } catch (error) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const finalAmount = Math.max(0, totalAmount - discountAmount);
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const remainingAmount = Math.max(0, finalAmount - totalPaid);

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    try {
      const response = await apiClient.get('/coupons');
      const coupons = response.data;
      const validCoupon = coupons.find(c => {
        if (c.code !== couponCode || !c.is_active) return false;
        if (c.expires_at && new Date(c.expires_at) < new Date()) return false;
        // Check max_uses limit
        if (c.max_uses !== null && c.max_uses !== undefined && c.used_count >= c.max_uses) return false;
        return true;
      });
      
      if (!validCoupon) {
        toast.error('Geçersiz, süresi dolmuş veya kullanım limitine ulaşmış kupon kodu');
        return;
      }
      
      setAppliedCoupon(validCoupon);
      let discount = 0;
      if (validCoupon.discount_type === 'percentage') {
        discount = totalAmount * (validCoupon.discount_value / 100);
      } else {
        discount = validCoupon.discount_value;
      }
      setDiscountAmount(Math.min(discount, totalAmount));
      toast.success('Kupon uygulandı!');
    } catch (error) {
      toast.error('Kupon kontrol edilirken hata oluştu');
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setDiscountAmount(0);
    setPayments([]); // Reset payments to prevent overpayment logic errors
  };

  const addPayment = (amount, method) => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return;
    
    if (amt > remainingAmount + 0.1) {
      toast.error('Ödenecek tutar kalan tutardan büyük olamaz');
      return;
    }
    
    setPayments([...payments, { amount: amt, method }]);
  };

  const handlePOSPayment = async (amount) => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return;
    if (amt > remainingAmount + 0.1) {
      toast.error('Ödenecek tutar kalan tutardan büyük olamaz');
      return;
    }

    if (posType === 'harici') {
      addPayment(amt, 'Kredi Kartı');
      return;
    }

    setIsWaitingPOS(true);
    toast.info(`POS Cihazına Tutar Gönderildi (${amt.toFixed(2)} TL). Lütfen Kartı Okutun...`, { duration: 4000 });

    try {
      if (window.electron && window.electron.sendPOSPayment) {
        const result = await window.electron.sendPOSPayment(amt, posType, posIp);
        if (result.success) {
          toast.success(result.message, { duration: 5000 });
          setPayments([...payments, { amount: amt, method: `Kredi Kartı (${posType.toUpperCase()})`, provizyon_kodu: result.provizyon }]);
        } else {
          toast.error('POS İşlemi Başarısız oldu.');
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const provizyon = Math.floor(100000 + Math.random() * 900000).toString();
        toast.success(`Ödeme başarıyla alındı! Provizyon Kodu: ${provizyon}`, { duration: 5000 });
        setPayments([...payments, { amount: amt, method: `Kredi Kartı (${posType.toUpperCase()})`, provizyon_kodu: provizyon }]);
      }
    } catch (error) {
      toast.error('POS ödemesi alınırken hata oluştu.');
    } finally {
      setIsWaitingPOS(false);
    }
  };

  const removePayment = (index) => {
    const newPayments = [...payments];
    newPayments.splice(index, 1);
    setPayments(newPayments);
  };

  const splitBy = (count) => {
    if (remainingAmount <= 0) return;
    const splitAmount = (Math.ceil((remainingAmount / count) * 100) / 100).toFixed(2);
    setPaymentAmountInput(splitAmount);
  };

  const handleCheckout = async () => {
    if (remainingAmount > 0.1) {
      toast.error('Hesabın tamamı ödenmeden kapatılamaz');
      return;
    }
    
    try {
      await apiClient.post(`/tables/${tableNumber}/checkout`, {
        payments,
        coupon_code: appliedCoupon ? appliedCoupon.code : null
      });
      toast.success('Hesap başarıyla kapatıldı');
      navigate('/admin/tables');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hesap kapatılamadı');
    }
  };

  if (loading) return <AdminLayout><div className="text-slate-900 dark:text-white p-8">Yükleniyor...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/orders')} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white">
              <ArrowLeft size={20} className="mr-2" />
              Geri Dön
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Masa {tableNumber} Hesap İşlemleri</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol Kolon - Siparişler ve Toplam */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Sipariş Özeti</h2>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {orders.map((order) => (
                  <div key={order.id} className="space-y-2 border-b border-slate-200 dark:border-slate-800 pb-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm text-slate-700 dark:text-slate-300">
                        <span>{item.quantity}x {item.menu_item_name}</span>
                        <span className="font-mono">{(item.price * item.quantity).toFixed(2)} ₺</span>
                      </div>
                    ))}
                  </div>
                ))}
                {orders.length === 0 && <p className="text-slate-500">Aktif sipariş bulunmuyor.</p>}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Ara Toplam</span>
                  <span className="font-mono">{totalAmount.toFixed(2)} ₺</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Kupon İndirimi ({appliedCoupon?.code})</span>
                    <span className="font-mono">-{discountAmount.toFixed(2)} ₺</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-900 dark:text-white font-bold text-xl pt-2 border-t border-slate-200 dark:border-slate-800">
                  <span>Ödenecek Tutar</span>
                  <span className="font-mono text-orange-400">{finalAmount.toFixed(2)} ₺</span>
                </div>
              </div>
            </Card>

            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                <Ticket className="mr-2 text-orange-400" size={20} />
                İndirim Kuponu
              </h2>
              {!appliedCoupon ? (
                <div className="flex space-x-2">
                  <Input 
                    placeholder="Kupon Kodu" 
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white uppercase"
                  />
                  <Button onClick={handleApplyCoupon} className="bg-orange-600 hover:bg-orange-700">
                    Uygula
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-green-500/10 border border-green-500/20 p-3 rounded-lg">
                  <div>
                    <p className="text-green-400 font-medium">{appliedCoupon.code}</p>
                    <p className="text-xs text-green-500/70">
                      {appliedCoupon.discount_type === 'percentage' 
                        ? `%${appliedCoupon.discount_value} İndirim` 
                        : `${appliedCoupon.discount_value} ₺ İndirim`}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRemoveCoupon} className="text-red-400 hover:text-red-300 hover:bg-red-400/10">
                    İptal
                  </Button>
                </div>
              )}
            </Card>
          </div>

          {/* Sağ Kolon - Ödeme İşlemleri */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Tahsilat Bilgileri</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl text-center">
                  <p className="text-slate-600 dark:text-slate-400 text-sm mb-1">Toplam Tutar</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white font-mono">{finalAmount.toFixed(2)} ₺</p>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-xl text-center">
                  <p className="text-orange-400/80 text-sm mb-1">Kalan Tutar</p>
                  <p className="text-2xl font-bold text-orange-400 font-mono">{remainingAmount.toFixed(2)} ₺</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-xl text-center">
                  <p className="text-green-400/80 text-sm mb-1">Ödenen</p>
                  <p className="text-xl font-bold text-green-400 font-mono">{totalPaid.toFixed(2)} ₺</p>
                </div>
              </div>

              {remainingAmount > 0 && (
                <div className="space-y-6">
                  <div className="flex space-x-2">
                    <Button variant="outline" onClick={() => splitBy(2)} className="flex-1 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:bg-slate-700 hover:text-slate-900 dark:text-white">
                      <Divide size={16} className="mr-2" /> 2'ye Böl
                    </Button>
                    <Button variant="outline" onClick={() => splitBy(3)} className="flex-1 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:bg-slate-700 hover:text-slate-900 dark:text-white">
                      <Divide size={16} className="mr-2" /> 3'e Böl
                    </Button>
                    <Button variant="outline" onClick={() => splitBy(4)} className="flex-1 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:bg-slate-700 hover:text-slate-900 dark:text-white">
                      <Divide size={16} className="mr-2" /> 4'e Böl
                    </Button>
                  </div>

                  <div className="flex space-x-4 items-end">
                    <div className="flex-1 space-y-2">
                      <label className="text-sm text-slate-600 dark:text-slate-400">Ödenecek Tutar (₺)</label>
                      <Input 
                        type="number" 
                        min="0"
                        step="0.01"
                        value={paymentAmountInput}
                        onChange={(e) => setPaymentAmountInput(e.target.value)}
                        placeholder={remainingAmount.toFixed(2)}
                        className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-lg font-mono"
                      />
                    </div>
                    
                    <Button 
                      onClick={() => addPayment(paymentAmountInput || remainingAmount, 'Nakit')}
                      disabled={isWaitingPOS}
                      className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6"
                    >
                      <Banknote size={18} className="mr-2" /> Nakit
                    </Button>
                    
                    <Button 
                      onClick={() => handlePOSPayment(paymentAmountInput || remainingAmount)}
                      disabled={isWaitingPOS}
                      className="bg-blue-600 hover:bg-blue-700 h-11 px-6"
                    >
                      <CreditCard size={18} className="mr-2" /> {isWaitingPOS ? 'POS Bekleniyor...' : 'Kart'}
                    </Button>
                  </div>
                </div>
              )}

              {payments.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3 border-b border-slate-200 dark:border-slate-800 pb-2">Alınan Ödemeler</h3>
                  <div className="space-y-2">
                    {payments.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-100 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                        <div className="flex items-center">
                          {p.method.includes('Nakit') ? <Banknote className="text-emerald-400 mr-3" size={18} /> : <CreditCard className="text-blue-400 mr-3" size={18} />}
                          <div>
                            <span className="text-slate-800 dark:text-slate-200 block">{p.method}</span>
                            {p.provizyon_kodu && <span className="text-xs text-orange-400 font-mono">Provizyon: {p.provizyon_kodu}</span>}
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className="font-mono text-slate-900 dark:text-white font-medium mr-4">{p.amount.toFixed(2)} ₺</span>
                          <Button variant="ghost" size="sm" onClick={() => removePayment(idx)} className="text-slate-500 hover:text-red-400 p-0 h-auto">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Button
              disabled={remainingAmount > 0.1}
              onClick={handleCheckout}
              className={`w-full h-14 text-lg font-bold ${
                remainingAmount <= 0.1 
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-900/20' 
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 cursor-not-allowed'
              }`}
            >
              <CheckCircle2 className="mr-2" size={24} />
              Hesabı Kapat
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CheckoutPage;
